<script setup lang="ts">
import CustomButton from "@/components/Button/Button.vue";
import {computed, onMounted, ref} from "vue";
import {getAffiliations} from "@/api";
import {IAffiliations} from "@/models/Affiliations";
import { useI18n } from 'vue-i18n';

const props = defineProps(['display'])
const display = computed(() => props.display)
const { t } = useI18n();
const affiliationTypes = {
  air: {
    title: t('affiliaties.aiport'),
    icon: "/assets/home/svg/plane.svg",
  },
  insurance: {
    title: t('affiliaties.insurance'),
    icon: "/assets/home/svg/insurance.svg",
  },
  provisioning: {
    title: t('affiliaties.provisioning'),
    icon: "/assets/home/svg/shopping.svg",
  },
  other: {
    title: t('affiliaties.other'),
    icon: "/assets/home/svg/generic.svg",
  },
}
const status = ref<'loading' | 'success' | 'error'>('loading');
const affiliations = ref<string,IAffiliations>({})

onMounted(() => {
  if (status.value === 'loading') {
    getAffiliations()
        .then(res => {
          affiliations.value = res.data
          status.value = 'success';
        })
        .catch(err => {
          status.value = 'error';
        })

  }
})

</script>
<template>
  <div v-if="display" class="overview-card bg-background-light p-7 rounded-11xl">
    <ElCollapse id="plan">
      <ElCollapseItem name="affiliations">
        <template #title>
          <div class="flex w-full">
            <div class="font-semibold">{{ t('affiliaties.next') }}</div>
          </div>
        </template>
        <div class="flex flex-col gap-4">
          <div v-for="(affiliation,key) of affiliations"
               v-bind:key="key">
            <div class="flex bg-white items-center p-3 rounded-8xl" v-if="affiliation.data.length === 1">
              <div class="icon-box flex justify-between gap-2 items-center w-full">
                <div class="flex gap-4">
                  <img class="h-13" :src="affiliationTypes[key].icon" alt="image"/>
                  <div class="flex flex-column">
                    <span class="icon-box__title responsive-title-6 font-bold">{{ affiliationTypes[key].title }}</span>
                    <span class="icon-box__subtitle  responsive-body-medium semibold text-bw-darkGrey">{{ affiliation.data[0].title }}</span>
                  </div>
                </div>
                <div>
                  <CustomButton
                      :is-rounded="true"
                      :is-outlined="true"
                      class="text-semibold bg-transparent px-0 py-0">
                    <a :href="affiliation.data[0].data"
                       v-if="affiliation.data[0].type === 'link'"
                       target="_blank"
                       class="req-insurance__button-inactive border border-black rounded-15xl px-4 py-1">
                      {{ t('affiliaties.go') }}
                    </a>
                  </CustomButton>
                </div>
              </div>
            </div>
            <div :id="`affiliate-group-${key}`" v-if="affiliation.data.length > 1"
                 class="affiliation-group flex bg-white p-3 rounded-8xl">
              <div class="icon-box flex flex-col gap-2 w-full">
                <div class="flex gap-4">
                  <img class="h-13" :src="affiliationTypes[key].icon" alt="image"/>
                  <div class="flex flex-column grow">
                    <div class="flex justify-space-between items-center">
                      <div>
                    <span class="icon-box__title responsive-title-6 font-bold">{{ affiliationTypes[key].title }}</span>
                      </div>
                      <CustomButton @click="affiliation.value.open = ! affiliation.value.open;"
                                    :rounded="true"
                                    :outlined="true"
                                    class="chev-btn affiliation-group-dropdown__button text-semibold" >
                        <img class="transition-transform" src="@/assets/icons/svg/chevron.svg" alt="arrow"
                             :style="affiliation.state ? {'transform': 'rotate(180deg)'} :  {'transform': 'rotate(270deg)' }"/>
                      </CustomButton>
                    </div>
                    <div
                        v-for="(affiliationItem,index) in affiliation.data"
                        v-bind:key="affiliationItem.id"
                        class="affiliation-group-content__results transition-all ease-in-out duration-500 "
                        :class="(affiliation.state ? 'close' : '')+ (index === affiliation.data.length-1 ? '': 'mt-4')">
                      <div class="affiliation-group-item flex justify-between align-center py-4 ">
                        <div class="flex gap-4 items-center">
                          <img :src="affiliationItem.icon" class="object-cover w-24 h-14" :alt="affiliationItem.title"/>
                          <div>{{affiliationItem.title}}</div>
                        </div>
                        <div>
                          <CustomButton :rounded="true" :outlined="true" class="px-4 py-1 provision-btn"
                               v-if="affiliationItem.type === 'link'"
                          >
                            <a :href="affiliationItem.data"
                                target="_blank">
                                <span class="text-semibold provision-btn">{{ t('affiliaties.go') }}</span>
                            </a>
                          </CustomButton>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ElCollapseItem>
    </ElCollapse>
  </div>
</template>
<style scoped>
.el-collapse {
  @apply border-t-0 border-r-0 border-b-0 flex-row-reverse;
}


.req-insurance__button::before {
  content: url("@/assets/icons/svg/Check_16x16.svg");
  position: absolute;
  left: 0;
  top: -15%;
  transform: translateY(25%);
}

.affiliation-group .affiliation-group-item {
  border-top: solid 1px #ccc;
}

.affiliation-group-content__results {
  overflow: hidden;
}

.affiliation-group-content__results.close {
  height: 0px;
  margin-top: 0 !important;
}


</style>